// client/src/pages/admin/Tracking.tsx
import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  Tag,
  Row,
  Col,
  Typography,
  Statistic,
  Alert,
  Switch,
  
  Progress,
  Badge,
  Input,
  
  
} from 'antd';
import {
  EnvironmentOutlined,
  CarOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  CompassOutlined,
  WarningOutlined,
  ReloadOutlined,
  FullscreenOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { trackingService, type VehicleLocation } from '../../services/trackingService';

const { Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// Fix Leaflet default icon issue with Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom vehicle markers by status
const getVehicleIcon = (status: string) => {
  const colors = {
    moving: '#52c41a',
    idle: '#fa8c16',
    stopped: '#1890ff'
  };
  const color = colors[status as keyof typeof colors] || '#666';

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

// Component to fit map bounds to all markers
const MapBounds: React.FC<{ locations: VehicleLocation[] }> = ({ locations }) => {
  const map = useMap();

  React.useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(
        locations.map(loc => [loc.latitude, loc.longitude])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);

  return null;
};

const AdminTracking: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterVehicleStatus, setFilterVehicleStatus] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [showMap, setShowMap] = useState(false);

  // Fetch vehicle locations with polling (30s)
  const { data: locationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['vehicleLocations', filterVehicleStatus],
    queryFn: () => {
      const filters: any = {};
      if (filterVehicleStatus !== 'all') {
        filters.vehicle_status = filterVehicleStatus;
      }
      return trackingService.getVehicleLocations(filters);
    },
    refetchInterval: autoRefresh ? 30000 : false, // Poll every 30 seconds
  });

  // Fetch tracking stats
  const { data: stats } = useQuery({
    queryKey: ['trackingStats'],
    queryFn: () => trackingService.getTrackingStats(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const locations = locationsData?.data || [];

  const getStatusColor = (status: string) => {
    const colors = {
      moving: 'green',
      idle: 'orange',
      stopped: 'blue'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      moving: 'En déplacement',
      idle: 'Au ralenti',
      stopped: 'Arrêté'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      moving: <PlayCircleOutlined />,
      idle: <PauseCircleOutlined />,
      stopped: <StopOutlined />
    };
    return icons[status as keyof typeof icons] || <StopOutlined />;
  };

  const getFuelColor = (level?: number) => {
    if (!level) return '#666';
    if (level < 20) return '#ff4d4f';
    if (level < 50) return '#fa8c16';
    return '#52c41a';
  };

  const filteredLocations = locations.filter(location => {
    const matchesStatus = filterStatus === 'all' || location.status === filterStatus;
    const matchesSearch = !searchText ||
      location.license_plate?.toLowerCase().includes(searchText.toLowerCase()) ||
      location.driver_first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      location.driver_last_name?.toLowerCase().includes(searchText.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const columns: ColumnsType<VehicleLocation> = [
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (_, record) => (
        <Space>
          <CarOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.license_plate || 'N/A'}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.brand} {record.model}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Conducteur',
      key: 'driver',
      render: (_, record) => (
        <div>
          {record.driver_first_name && record.driver_last_name ? (
            <>
              <div>{record.driver_first_name} {record.driver_last_name}</div>
              {record.driver_phone && (
                <div style={{ fontSize: '12px', color: '#666' }}>{record.driver_phone}</div>
              )}
            </>
          ) : (
            <Text type="secondary">Aucun conducteur</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Statut',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color={getStatusColor(record.status)} icon={getStatusIcon(record.status)}>
            {getStatusText(record.status)}
          </Tag>
          {record.mission_id && (
            <Tag color="purple">
              Mission #{record.mission_number || record.mission_id.slice(0, 8)}
            </Tag>
          )}
          {record.is_recent === false && (
            <Tag color="orange" icon={<WarningOutlined />}>
              Position ancienne
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Position actuelle',
      key: 'location',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <EnvironmentOutlined style={{ marginRight: '4px' }} />
            {record.address || 'Adresse non disponible'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.latitude.toFixed(4)}, {record.longitude.toFixed(4)}
          </div>
        </div>
      ),
    },
    {
      title: 'Vitesse',
      key: 'speed',
      render: (_, record) => (
        <Space>
          <DashboardOutlined />
          <span style={{ color: (record.speed || 0) > 50 ? '#ff4d4f' : '#666' }}>
            {record.speed?.toFixed(0) || 0} km/h
          </span>
        </Space>
      ),
    },
    {
      title: 'Carburant',
      key: 'fuel',
      render: (_, record) => (
        record.fuel_level ? (
          <div>
            <Progress
              percent={record.fuel_level}
              size="small"
              strokeColor={getFuelColor(record.fuel_level)}
              format={(percent) => `${percent}%`}
            />
          </div>
        ) : (
          <Text type="secondary">N/A</Text>
        )
      ),
    },
    {
      title: 'Direction',
      key: 'heading',
      render: (_, record) => (
        record.heading !== undefined && record.heading !== null ? (
          <Space>
            <CompassOutlined style={{ transform: `rotate(${record.heading}deg)` }} />
            {record.heading.toFixed(0)}°
          </Space>
        ) : (
          <Text type="secondary">N/A</Text>
        )
      ),
    },
    {
      title: 'Dernière mise à jour',
      dataIndex: 'recorded_at',
      key: 'recorded_at',
      render: (date: string, record) => {
        const minutesAgo = record.minutes_ago || Math.floor((Date.now() - new Date(date).getTime()) / 60000);
        return (
          <div>
            <div>{dayjs(date).format('HH:mm:ss')}</div>
            <div style={{ fontSize: '12px', color: minutesAgo > 15 ? '#ff4d4f' : '#666' }}>
              Il y a {minutesAgo} min
            </div>
          </div>
        );
      },
    },
  ];

  // Handle error
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Erreur de chargement"
          description="Impossible de charger les données de tracking. Veuillez réessayer."
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={() => refetch()}>
              Réessayer
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Statistiques en temps réel */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Véhicules actifs"
              value={stats?.actively_tracked || 0}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={`/ ${stats?.total_vehicles || 0}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="En déplacement"
              value={stats?.moving || 0}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="En mission"
              value={stats?.in_mission || 0}
              prefix={<EnvironmentOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Au ralenti / Arrêtés"
              value={(stats?.idle || 0) + (stats?.stopped || 0)}
              prefix={<PauseCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Contrôles */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={6}>
            <Space>
              <Text strong>Actualisation auto:</Text>
              <Switch
                checked={autoRefresh}
                onChange={setAutoRefresh}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </Space>
          </Col>
          <Col xs={24} sm={5}>
            <Select
              placeholder="Filtrer par statut GPS"
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
            >
              <Option value="all">Tous les statuts GPS</Option>
              <Option value="moving">En déplacement</Option>
              <Option value="idle">Au ralenti</Option>
              <Option value="stopped">Arrêtés</Option>
            </Select>
          </Col>
          <Col xs={24} sm={5}>
            <Select
              placeholder="Filtrer par statut véhicule"
              value={filterVehicleStatus}
              onChange={setFilterVehicleStatus}
              style={{ width: '100%' }}
            >
              <Option value="all">Tous les véhicules</Option>
              <Option value="active">Actifs</Option>
              <Option value="inactive">Inactifs</Option>
              <Option value="maintenance">En maintenance</Option>
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Search
              placeholder="Rechercher..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={4}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isLoading}
              >
                Actualiser
              </Button>
              <Button
                icon={<FullscreenOutlined />}
                onClick={() => setShowMap(!showMap)}
                type={showMap ? 'primary' : 'default'}
              >
                {showMap ? 'Table' : 'Carte'}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Vue Carte */}
      {showMap && filteredLocations.length > 0 && (
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ height: '500px', width: '100%' }}>
            <MapContainer
              center={[filteredLocations[0].latitude, filteredLocations[0].longitude]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapBounds locations={filteredLocations} />
              {filteredLocations.map((location) => (
                <Marker
                  key={location.id}
                  position={[location.latitude, location.longitude]}
                  icon={getVehicleIcon(location.status)}
                >
                  <Popup>
                    <div style={{ minWidth: '200px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        {location.license_plate} - {location.brand} {location.model}
                      </div>
                      {location.driver_first_name && (
                        <div>Conducteur: {location.driver_first_name} {location.driver_last_name}</div>
                      )}
                      <div>Statut: {getStatusText(location.status)}</div>
                      <div>Vitesse: {location.speed?.toFixed(0) || 0} km/h</div>
                      {location.fuel_level && (
                        <div>Carburant: {location.fuel_level}%</div>
                      )}
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                        Mise à jour: {dayjs(location.recorded_at).format('HH:mm:ss')}
                      </div>
                      {location.address && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                          {location.address}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </Card>
      )}

      {/* Tableau de suivi */}
      {!showMap && (
        <Card
          title={
            <Space>
              <EnvironmentOutlined />
              Suivi GPS en temps réel
              <Badge count={filteredLocations.length} style={{ backgroundColor: '#52c41a' }} />
              {autoRefresh && (
                <Tag color="green" icon={<ClockCircleOutlined />}>
                  Mise à jour auto (30s)
                </Tag>
              )}
            </Space>
          }
          extra={
            <Text type="secondary">
              Dernière actualisation: {dayjs().format('HH:mm:ss')}
            </Text>
          }
        >
          <Table
            columns={columns}
            dataSource={filteredLocations}
            rowKey="id"
            loading={isLoading}
            pagination={{
              total: filteredLocations.length,
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} véhicules`,
            }}
            rowClassName={(record) => {
              if (record.is_recent === false) return 'row-old-data';
              return '';
            }}
          />
        </Card>
      )}

      {/* Légende */}
      <Card
        title="Légende"
        size="small"
        style={{ marginTop: '16px' }}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Space wrap>
              <Tag color="green" icon={<PlayCircleOutlined />}>En déplacement</Tag>
              <Tag color="orange" icon={<PauseCircleOutlined />}>Au ralenti</Tag>
              <Tag color="blue" icon={<StopOutlined />}>Arrêté</Tag>
              <Tag color="purple">Mission en cours</Tag>
            </Space>
          </Col>
          <Col xs={24} md={12}>
            <Space wrap>
              <Tag color="orange" icon={<WarningOutlined />}>Position ancienne (&gt;15 min)</Tag>
              <div style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block', marginRight: '4px' }} />
                En déplacement (carte)
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fa8c16', display: 'inline-block', marginRight: '4px' }} />
                Au ralenti (carte)
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* CSS intégré pour les styles des lignes */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .row-old-data {
            opacity: 0.7;
            background-color: #fff7e6;
          }
          .leaflet-container {
            z-index: 1;
          }
        `
      }} />
    </div>
  );
};

export default AdminTracking;
